// ─────────────────────────────────────────────────────────
// components/ConfirmDialog.tsx — Confirmation dialogs
//
// Phase 2 changes:
//   + Uses @radix-ui/react-alert-dialog as the a11y primitive:
//       - Proper alertdialog ARIA role
//       - Focus managed to action button
//       - Escape key built-in
//       - Portal rendering built-in
//   - Removed: manual createPortal, manual Escape listener
//
// External API: UNCHANGED — useConfirm() hook, same return type.
// Visual output: IDENTICAL.
// ─────────────────────────────────────────────────────────
import { useState, useCallback, type ReactNode } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'

interface ConfirmOptions {
  msg:      string
  title?:   string
  onOk:     () => void
  danger?:  boolean
  okLabel?: string
}

interface ConfirmDialogProps {
  open:    boolean
  options: ConfirmOptions | null
  onClose: () => void
}

// ── Shared button inline styles (same as Phase 1) ─────────
const baseBtnStyle: React.CSSProperties = {
  display:      'inline-flex',
  alignItems:   'center',
  gap:          5,
  padding:      '6px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize:     12,
  fontWeight:   600,
  cursor:       'pointer',
  fontFamily:   'var(--font-body)',
}

function ConfirmDialogUI({ open, options, onClose }: ConfirmDialogProps) {
  if (!options) return null

  const { msg, title, onOk, danger = false, okLabel } = options
  const defaultTitle  = danger ? '⚠ Are you sure?' : 'Confirm'
  const defaultOkText = danger ? 'Delete' : 'Confirm'
  const okBtnLabel    = okLabel ?? defaultOkText

  const handleOk = () => { onClose(); onOk() }

  return (
    <AlertDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <AlertDialog.Portal>
        {/* Overlay */}
        <AlertDialog.Overlay
          style={{
            position:   'fixed',
            inset:      0,
            background: 'rgba(0,0,0,.45)',
            zIndex:     9998,
            animation:  'page-in .15s ease',
          }}
        />

        {/* Content */}
        <AlertDialog.Content
          style={{
            position:     'fixed',
            top:          '50%',
            left:         '50%',
            transform:    'translate(-50%, -50%)',
            zIndex:       9999,
            background:   'var(--color-bg)',
            border:       '1px solid var(--color-bd)',
            borderRadius: 8,
            boxShadow:    'var(--shadow-xl)',
            maxWidth:     400,
            width:        'calc(100% - 32px)',
            padding:      '22px 24px',
            animation:    'page-in .15s ease',
          }}
        >
          {/* Title */}
          <AlertDialog.Title style={{
            fontSize:     14,
            fontWeight:   700,
            color:        danger ? 'var(--color-neg)' : 'var(--color-tx)',
            marginBottom: 8,
            margin:       '0 0 8px',
          }}>
            {title ?? defaultTitle}
          </AlertDialog.Title>

          {/* Message */}
          <AlertDialog.Description style={{
            fontSize:     13,
            color:        'var(--color-tx2)',
            lineHeight:   1.6,
            marginBottom: 18,
            whiteSpace:   'pre-wrap',
          }}>
            {msg}
          </AlertDialog.Description>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <AlertDialog.Cancel asChild>
              <button
                onClick={onClose}
                style={{
                  ...baseBtnStyle,
                  border:     '1px solid var(--color-bd)',
                  background: 'var(--color-bg)',
                  color:      'var(--color-tx2)',
                }}
              >Cancel</button>
            </AlertDialog.Cancel>

            <AlertDialog.Action asChild>
              <button
                onClick={handleOk}
                style={{
                  ...baseBtnStyle,
                  fontWeight: 700,
                  border:     'none',
                  background: danger ? 'var(--color-neg)' : 'var(--color-ac)',
                  color:      '#fff',
                }}
              >{okBtnLabel}</button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

// ── useConfirm hook — identical signature to Phase 1 ──────
export function useConfirm(): [
  (options: ConfirmOptions) => void,
  ReactNode,
] {
  const [open,    setOpen]    = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts)
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => setOpen(false), [])

  const dialog = (
    <ConfirmDialogUI open={open} options={options} onClose={handleClose} />
  )

  return [confirm, dialog]
}

export default ConfirmDialogUI
